import { Component, ChangeDetectionStrategy, inject, OnInit, OnDestroy, computed, output, signal } from '@angular/core';
import { Button } from 'primeng/button';
import { Message } from 'primeng/message';
import { Card } from 'primeng/card';
import { TranslocoModule } from '@jsverse/transloco';
import { PropertyWizardStateService, WizardValidationService, UnitTypeValidationMap } from '../../services';
import { UnitTypeCardComponent } from './unit-type-card/unit-type-card.component';
import { WizardStepHintComponent } from '../wizard-step-hint/wizard-step-hint.component';
import { GalleryUploadState } from '../shared/gallery-upload/gallery-upload.component';
import { UnitType } from '../../models';
import { WIZARD_LIMITS } from '../../config/wizard-limits';

@Component({
  selector: 'app-step-unit-types',
  standalone: true,
  imports: [Button, Message, Card, TranslocoModule, UnitTypeCardComponent, WizardStepHintComponent],
  templateUrl: './step-unit-types.component.html',
  styleUrl: './step-unit-types.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StepUnitTypesComponent implements OnInit, OnDestroy {
  private readonly stateService = inject(PropertyWizardStateService);
  private readonly validationService = inject(WizardValidationService);

  readonly previous = output<void>();
  readonly next = output<void>();

  readonly unitTypes = this.stateService.unitTypes;
  readonly isViewMode = this.stateService.isViewMode;
  readonly canGoNext = this.stateService.canGoNext;

  readonly limits = WIZARD_LIMITS;
  readonly totalImages = this.stateService.totalImages;
  readonly isGlobalImageLimitReached = this.stateService.isGlobalImageLimitReached;

  readonly canAddMore = computed(() => this.unitTypes().length < WIZARD_LIMITS.MAX_UNIT_TYPES);
  readonly canRemove = computed(() => true);

  readonly lastAddedId = signal<string | null>(null);

  /** Upload state per unit card — gates "Next" while any gallery is uploading or has errors. */
  private readonly cardUploadStates = new Map<string, GalleryUploadState>();

  readonly unitErrorsMap = computed<UnitTypeValidationMap>(() =>
    this.validationService.getUnitTypeErrorsMap(this.unitTypes())
  );

  readonly hasAnyErrors = computed(() =>
    Object.keys(this.unitErrorsMap()).length > 0
  );

  getUnitErrors(unitId: string): string[] {
    return this.unitErrorsMap()[unitId] ?? [];
  }

  ngOnInit(): void {
    this.updateValidity();
  }

  ngOnDestroy(): void {
    this.updateValidity();
  }

  addUnitType(): void {
    const id = this.generateId();
    const newUnit: UnitType = {
      id,
      name: '',
      description: '',
      propertyTypeId: null,
      quantity: 1,
      maxOccupancy: 2,
      namingPattern: '',
      namingMode: 'auto',
      unitNames: [],
      images: [],
      amenityIds: []
    };
    this.stateService.addUnitType(newUnit);
    this.lastAddedId.set(id);
    this.updateValidity();
  }

  onUnitChange(unit: UnitType): void {
    this.stateService.updateUnitType(unit);
    this.updateValidity();
  }

  onRemoveUnit(unitId: string): void {
    this.stateService.removeUnitType(unitId);
    this.cardUploadStates.delete(unitId);
    if (this.lastAddedId() === unitId) {
      this.lastAddedId.set(null);
    }
    this.updateValidity();
  }

  onCardUploadState(unitId: string, state: GalleryUploadState): void {
    this.cardUploadStates.set(unitId, state);
    this.updateValidity();
  }

  private updateValidity(): void {
    const result = this.validationService.validateUnitTypes(this.unitTypes());
    // Block the step while any unit's gallery is still uploading or has failed uploads.
    const allSettled = this.unitTypes().every(
      (u) => this.cardUploadStates.get(u.id)?.settled ?? true,
    );
    this.stateService.setStepValidity(1, result.isValid && allSettled);
  }

  private generateId(): string {
    return `unit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  trackByUnitId(index: number, unit: UnitType): string {
    return unit.id;
  }

  onPrevious(): void {
    this.stateService.previousStep();
    this.previous.emit();
  }

  onNext(): void {
    this.stateService.nextStep();
    this.next.emit();
  }
}
